import React, { useState, useEffect } from 'react';

function WeekView({ events, getEventsForDate, getCategoryStyles, onEditEvent, onDateSelect, isMobile }) {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  
  // Get the start of the week (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };
  
  // Get all days of the week
  const getWeekDays = (startDate) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };
  
  // Time slots from 6 AM to 11 PM
  const timeSlots = [];
  for (let hour = 6; hour <= 23; hour++) {
    timeSlots.push(hour);
  }
  
  const weekStart = getWeekStart(currentWeek);
  const weekDays = getWeekDays(weekStart);
  
  // Navigate to previous week
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeek(newDate);
  };
  
  // Navigate to next week
  const goToNextWeek = () => {
    const newDate = new Date(currentWeek);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeek(newDate);
  };
  
  // Get events for a specific hour slot
  const getEventsForHour = (date, hour) => {
    const dayEvents = getEventsForDate(date, events);
    return dayEvents.filter(event => {
      if (event.isAllDay) return false;
      // Include events that start in this hour or span across it
      const eventStartHour = event.hour || 0;
      const eventEndHour = event.endHour || eventStartHour + 1;
      return eventStartHour <= hour && hour < eventEndHour;
    });
  };
  
  // Get all-day events for a date
  const getAllDayEvents = (date) => {
    const dayEvents = getEventsForDate(date, events);
    return dayEvents.filter(event => event.isAllDay);
  };
  
  // Format time display
  const formatHour = (hour, showAmPm = true) => {
    if (hour === 0) return showAmPm ? '12 AM' : '12';
    if (hour === 12) return showAmPm ? '12 PM' : '12';
    if (hour < 12) return showAmPm ? `${hour} AM` : `${hour}`;
    return showAmPm ? `${hour - 12} PM` : `${hour - 12}`;
  };
  
  // Calculate event position and height
  const getEventStyle = (event, hour) => {
    const eventStartHour = event.hour || 0;
    const eventStartMinute = event.minute || 0;
    // If no end time, assume 1 hour duration
    const eventEndHour = event.endHour || (eventStartHour + 1);
    const eventEndMinute = event.endMinute || eventStartMinute;
    
    // Calculate if event starts in this hour slot
    let topPosition = 0;
    if (eventStartHour === hour) {
      topPosition = (eventStartMinute / 60) * 100;
    }
    
    // Calculate height based on duration
    let heightPercent = 100;
    if (eventStartHour === hour && eventEndHour === hour) {
      // Event starts and ends in same hour
      const durationMinutes = eventEndMinute - eventStartMinute;
      heightPercent = (durationMinutes / 60) * 100;
    } else if (eventStartHour === hour && eventEndHour > hour) {
      // Event starts in this hour but ends later
      const durationMinutes = 60 - eventStartMinute;
      heightPercent = (durationMinutes / 60) * 100;
    } else if (eventStartHour < hour && eventEndHour > hour) {
      // Event spans entire hour
      heightPercent = 100;
    } else if (eventStartHour < hour && eventEndHour === hour) {
      // Event ends in this hour
      heightPercent = (eventEndMinute / 60) * 100;
    }
    
    return {
      top: `${topPosition}%`,
      height: `${Math.max(heightPercent, 20)}%`, // Minimum height for visibility
      minHeight: '25px'
    };
  };
  
  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };
  
  return (
    <div className="week-view-container" style={{
      width: '100%',
      marginTop: '20px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      padding: isMobile ? '10px 5px' : '20px',
      overflowX: isMobile ? 'hidden' : 'auto',
      color: '#000000',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    }}>
      {/* Week navigation */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '20px',
        gap: isMobile ? '20px' : '40px'
      }}>
        <button
          onClick={goToPreviousWeek}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '30px',
            height: '30px',
            cursor: 'pointer'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        
        <h3 style={{
          margin: 0,
          fontSize: isMobile ? '1rem' : '1.2rem'
        }}>
          {weekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </h3>
        
        <button
          onClick={goToNextWeek}
          style={{
            background: 'transparent',
            border: 'none',
            padding: '0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '30px',
            height: '30px',
            cursor: 'pointer'
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      </div>
      
      {/* Week grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '35px repeat(7, minmax(0, 1fr))' : '60px repeat(7, minmax(0, 1fr))',
        gap: '0',
        backgroundColor: '#e0e0e0',
        width: '100%',
        maxWidth: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Header row - Days of week */}
        <div style={{ backgroundColor: '#f5f5f5', padding: isMobile ? '5px' : '10px' }}></div>
        {weekDays.map((day, index) => (
          <div
            key={index}
            style={{
              backgroundColor: '#f5f5f5',
              padding: isMobile ? '5px 2px' : '10px',
              textAlign: 'center',
              fontSize: isMobile ? '0.7rem' : '0.9rem',
              fontWeight: '500',
              borderRight: index < 6 ? '1px solid #e0e0e0' : 'none'
            }}
          >
            {isMobile ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '600' }}>
                  {day.toLocaleDateString('en-US', { weekday: 'short' }).charAt(0)}
                </div>
                <div style={{ 
                  fontSize: '0.65rem', 
                  fontWeight: isToday(day) ? 'bold' : 'normal',
                  color: isToday(day) ? '#ff4444' : 'inherit'
                }}>
                  {day.getDate()}
                </div>
              </div>
            ) : (
              <>
                <div>{day.toLocaleDateString('en-US', { weekday: 'short' })} {day.getDate()}</div>
                {isToday(day) && (
                  <div style={{
                    width: '30px',
                    height: '30px',
                    backgroundColor: '#ff4444',
                    borderRadius: '50%',
                    margin: '5px auto 0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.8rem',
                    fontWeight: 'bold'
                  }}>
                    {day.getDate()}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        
        {/* All-day events row */}
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: isMobile ? '5px' : '10px',
          fontSize: isMobile ? '0.65rem' : '0.8rem',
          color: '#666',
          borderTop: '1px solid #e0e0e0'
        }}>
          {isMobile ? 'all' : 'all-day'}
        </div>
        {weekDays.map((day, dayIndex) => (
          <div
            key={`allday-${dayIndex}`}
            style={{
              backgroundColor: '#f5f5f5',
              padding: isMobile ? '2px' : '5px',
              minHeight: isMobile ? '30px' : '40px',
              position: 'relative',
              borderTop: '1px solid #e0e0e0',
              borderRight: dayIndex < 6 ? '1px solid #e0e0e0' : 'none'
            }}
          >
            {getAllDayEvents(day).map((event, eventIndex) => (
              <div
                key={event.id || eventIndex}
                onClick={() => onDateSelect ? onDateSelect(day, event.id) : onEditEvent(event)}
                style={{
                  backgroundColor: getCategoryStyles(event.category).color || '#4361ee',
                  color: '#ffffff',
                  padding: isMobile ? '1px 2px' : '4px 8px',
                  marginBottom: '2px',
                  borderRadius: '4px',
                  fontSize: isMobile ? '0.5rem' : '0.75rem',
                  cursor: 'pointer',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: '1.2'
                }}
              >
                {event.eventName}
              </div>
            ))}
          </div>
        ))}
        
        {/* Time slots */}
        {timeSlots.map((hour) => (
          <React.Fragment key={hour}>
            {/* Time label */}
            <div style={{
              backgroundColor: '#f5f5f5',
              padding: isMobile ? '15px 2px' : '20px 10px',
              fontSize: isMobile ? '0.55rem' : '0.8rem',
              color: '#666',
              borderTop: '1px solid #e0e0e0',
              textAlign: 'right'
            }}>
              {formatHour(hour)}
            </div>
            
            {/* Hour slots for each day */}
            {weekDays.map((day, dayIndex) => (
              <div
                key={`${hour}-${dayIndex}`}
                onClick={() => {
                  // Only trigger if clicking on empty space (not on an event)
                  if (getEventsForHour(day, hour).length === 0) {
                    onDateSelect(day, null, hour);
                  }
                }}
                style={{
                  backgroundColor: '#ffffff',
                  minHeight: isMobile ? '50px' : '60px',
                  position: 'relative',
                  borderTop: '1px solid #e0e0e0',
                  borderRight: dayIndex < 6 ? '1px solid #e0e0e0' : 'none',
                  cursor: getEventsForHour(day, hour).length === 0 ? 'pointer' : 'default',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (getEventsForHour(day, hour).length === 0) {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                {getEventsForHour(day, hour).map((event, eventIndex) => {
                  const eventStyle = getEventStyle(event, hour);
                  return (
                    <div
                      key={event.id || eventIndex}
                      onClick={() => onDateSelect ? onDateSelect(day, event.id) : onEditEvent(event)}
                      style={{
                        position: 'absolute',
                        left: '2px',
                        right: '2px',
                        backgroundColor: getCategoryStyles(event.category).color || '#4361ee',
                        color: '#ffffff',
                        padding: isMobile ? '1px 2px' : '4px 6px',
                        borderRadius: '4px',
                        fontSize: isMobile ? '0.5rem' : '0.75rem',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        zIndex: 1,
                        ...eventStyle
                      }}
                    >
                      {isMobile ? (
                        <div style={{ fontSize: '0.5rem', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.2' }}>
                          {event.eventName}
                        </div>
                      ) : (
                        <>
                          <div style={{ fontWeight: '500', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.2' }}>{event.eventName}</div>
                          <div style={{ fontSize: '0.7em', opacity: 0.8 }}>
                            {formatHour(event.hour || 0, false)}:{(event.minute || 0).toString().padStart(2, '0')}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      
      {/* Current time indicator */}
      <style>
        {`
          .week-view-container::-webkit-scrollbar {
            height: 8px;
          }
          
          .week-view-container::-webkit-scrollbar-track {
            background: #f0f0f0;
            border-radius: 4px;
          }
          
          .week-view-container::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 4px;
          }
          
          .week-view-container::-webkit-scrollbar-thumb:hover {
            background: #aaa;
          }
        `}
      </style>
    </div>
  );
}

export default WeekView;